import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchAdminMe,
  fetchAdminAnnonces,
  bulkAdminAnnonces,
} from "@/lib/api";
import {
  Search, Loader2, ShieldCheck, CheckSquare, Square, Download,
} from "lucide-react";
import EditAnnonceModal from "@/components/admin/EditAnnonceModal";
import BulkToolbar from "@/components/admin/BulkToolbar";
import ExportAnnoncesModal from "@/components/admin/ExportAnnoncesModal";
import { withWatermark } from "@/lib/utils";

export default function AdminAnnoncesPage() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    fetchAdminMe()
      .then(() => setAuthChecked(true))
      .catch(() => navigate("/", { replace: true }));
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAdminAnnonces(q || undefined);
      setAnnonces(data);
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    if (authChecked) load();
  }, [authChecked, load]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => {
      if (prev.size === annonces.length) return new Set();
      return new Set(annonces.map((a) => a.id));
    });
  };

  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action, patch) => {
    if (!selected.size) return;
    if (action === "delete" && !window.confirm(`Supprimer definitivement ${selected.size} annonce(s) ?`)) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selected);
      const r = await bulkAdminAnnonces(ids, action, patch);
      if (action === "delete") {
        setAnnonces((prev) => prev.filter((a) => !selected.has(a.id)));
        showToast(`${r.deleted} annonce(s) supprimee(s)`);
      } else {
        setAnnonces((prev) => prev.map((a) => (selected.has(a.id) ? { ...a, ...patch } : a)));
        showToast(`${r.modified} annonce(s) mise(s) a jour`);
      }
      clearSelection();
    } catch (e) {
      showToast(e?.response?.data?.detail || e.message || "Erreur", "error");
    } finally {
      setBulkBusy(false);
    }
  };

  if (!authChecked) {
    return (
      <main className="max-w-7xl mx-auto px-6 py-20 text-center">
        <Loader2 className="animate-spin mx-auto text-orange-500" />
        <p className="mt-3 text-[var(--ink-soft)]">Verification des droits...</p>
      </main>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-annonces-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight inline-flex items-center gap-3">
            <ShieldCheck size={32} className="text-orange-500" /> Annonces (admin)
          </h1>
          <p className="text-[var(--ink-soft)] mt-2">
            <b className="text-orange-600">{annonces.length}</b> annonce{annonces.length > 1 ? "s" : ""} -
            cliquez sur une carte pour modifier les details ou changer les photos.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setExportOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border border-[var(--line)] bg-white hover:bg-orange-50"
          data-testid="open-export-btn"
        >
          <Download size={14} /> Exporter CSV
        </button>
      </div>

      <div className="relative mt-6 mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher par titre, ville, categorie ou ID..."
            className="w-full pl-10 pr-4 py-2.5 rounded-full border border-[var(--line)] focus:outline-none focus:ring-2 focus:ring-orange-300 text-sm bg-white"
            data-testid="admin-search"
          />
        </div>
        {annonces.length > 0 && (
          <button
            type="button"
            onClick={toggleSelectAll}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border border-[var(--line)] bg-white hover:bg-orange-50"
            data-testid="admin-select-all"
          >
            {selected.size === annonces.length && annonces.length > 0 ? <CheckSquare size={14} /> : <Square size={14} />}
            {selected.size === annonces.length && annonces.length > 0 ? "Tout deselectionner" : "Tout selectionner"}
          </button>
        )}
      </div>

      {loading && <div className="glass-card p-10 text-center">Chargement...</div>}
      {!loading && annonces.length === 0 && (
        <div className="glass-card p-10 text-center text-[var(--ink-soft)]">Aucune annonce.</div>
      )}
      {!loading && annonces.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-32">
          {annonces.map((a) => (
            <AnnonceCard
              key={a.id}
              annonce={a}
              isSelected={selected.has(a.id)}
              onToggle={() => toggleSelect(a.id)}
              onEdit={() => setEditing(a)}
            />
          ))}
        </div>
      )}

      {selected.size > 0 && (
        <BulkToolbar count={selected.size} busy={bulkBusy} onClear={clearSelection} onAction={runBulk} />
      )}

      {editing && (
        <EditAnnonceModal
          annonce={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setAnnonces((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
            setEditing(null);
            showToast("Annonce mise a jour");
          }}
          onDeleted={(id) => {
            setAnnonces((prev) => prev.filter((x) => x.id !== id));
            setEditing(null);
            showToast("Annonce supprimee");
          }}
        />
      )}

      {exportOpen && (
        <ExportAnnoncesModal
          onClose={() => setExportOpen(false)}
          onDone={() => { setExportOpen(false); showToast("Export telecharge"); }}
        />
      )}

      {toast && (
        <div
          className={`fixed bottom-6 right-6 px-4 py-3 rounded-xl shadow-xl text-white font-semibold z-50 ${
            toast.type === "error" ? "bg-red-500" : "bg-green-500"
          }`}
          data-testid="admin-toast"
        >
          {toast.msg}
        </div>
      )}
    </main>
  );
}

function AnnonceCard({ annonce: a, isSelected, onToggle, onEdit }) {
  return (
    <div
      className={`glass-card p-3 flex gap-3 transition-all relative ${
        isSelected ? "ring-2 ring-orange-400 bg-orange-50/30" : ""
      }`}
      data-testid={`admin-annonce-${a.id}`}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        className="absolute top-2 left-2 w-6 h-6 rounded-md bg-white border border-[var(--line)] grid place-items-center hover:border-orange-400 z-10"
        data-testid={`admin-select-${a.id}`}
        aria-label="Selectionner"
      >
        {isSelected ? <CheckSquare size={14} className="text-orange-500" /> : <Square size={14} className="text-[var(--ink-soft)]" />}
      </button>
      <button
        type="button"
        onClick={onEdit}
        className="flex gap-3 flex-1 text-left hover:translate-y-[-1px] transition-transform"
        data-testid={`admin-edit-${a.id}`}
      >
        <img
          src={withWatermark(a.image) || "/placeholder.png"}
          alt=""
          className="w-24 h-24 rounded-xl object-cover bg-orange-50 shrink-0 ml-6"
        />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)] font-bold">#{a.id} - {a.category}</div>
          <div className="font-bold truncate mt-0.5">{a.title}</div>
          <div className="text-xs text-[var(--ink-soft)] truncate">{a.city}{a.quartier ? ` - ${a.quartier}` : ""}</div>
          <div className="text-sm font-bold text-orange-600 mt-1">
            {(a.price || 0).toLocaleString("fr-FR")} FCFA
          </div>
          <div className="flex gap-1.5 mt-1">
            {a.premium && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-bold">PREMIUM</span>}
            {a.verified && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-bold">VERIFIE</span>}
          </div>
        </div>
      </button>
    </div>
  );
}
